import { useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { Form, Row, Col, Spinner } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { toast } from "react-toastify";
import { BASE_URL, HEADERS } from "../../../utils/constant";

const STATUS_CHOICES = [
  { value: "Inactive", label: "Inactive" },
  { value: "Interact", label: "Interact" },
  { value: "In-Discussion", label: "In-Discussion" },
  { value: "Quote Sent", label: "Quote Sent" },
  { value: "Service Taken", label: "Service Taken" },
  { value: "Not Interested", label: "Not Interested" },
];

function ExportModal({
  show,
  handleClose,
  title,
  selectedTicketIds,
  attendanceid,
  technicianId,
  setSelectedTicketIds,
  selectedInvoiceIds,
  setSelectedInvoiceIds,
}) {
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [statusFilter, setStatusFilter] = useState("");
  const [exportLoading, setExportLoading] = useState(false);

  const handleExport = async (isExportAll = false) => {
    setExportLoading(true);

    const apiEndpoints = {
      agents: "/api/export-agents/",
      stocks: "/api/export-stocks/",
      amcs: "/api/export-amcs/",
      customers: "/api/export-customers/",
      subcustomers: "/api/export-subcustomers/",
      leads: "/api/export-leads/",
      sales: "/api/export-sales/",
      expenses: `/api/export-expenses/?user_id=${technicianId}`,
      superusers: "/api/export-superusers/",
      requests: "/api/export-service-requests/",
      technicians: "/api/export-technicians/",
      tickets: "/api/export-tickets/",
      allAttendence: "/api/export-all-users-attendance/",
      attendence: `/api/export-attendance/${attendanceid}/`,
      leaves: `/users/export-leaves/`,
      invoices: "/users/subscriptions/export-invoices/",
    };

    const endpoint = apiEndpoints[title] || null;

    if (!endpoint) {
      toast.error("Invalid export type.");
      setExportLoading(false);
      return;
    }

    try {
      let params = null;

      if (!isExportAll) {
        if (
          title.toLowerCase() === "tickets" &&
          selectedTicketIds?.length > 0
        ) {
          params = {
            ticket_ids: selectedTicketIds.join(","),
          };
        } else if (
          title.toLowerCase() === "invoices" &&
          selectedInvoiceIds?.length > 0
        ) {
          params = {
            invoice_ids: selectedInvoiceIds.join(","),
          };
        } else {
          params = {
            start_date: startDate
              ? startDate.toISOString().split("T")[0]
              : undefined,
            end_date: endDate ? endDate.toISOString().split("T")[0] : undefined,
          };

          if (title.toLowerCase() === "leads" && statusFilter) {
            params.status = statusFilter;
          }
        }
      }

      const response = await axios.get(`${BASE_URL}${endpoint}`, {
        params: isExportAll ? undefined : params,
        headers: HEADERS,
        responseType: "blob",
      });
      if (!response.data || response.data.size === 0) {
        toast.error("No data found.");
        return;
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${title}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Export successful!");
      handleClose();
      if (title.toLowerCase() === "tickets") {
        setSelectedTicketIds([]);
      }

      if (title.toLowerCase() === "invoices") {
        setSelectedInvoiceIds([]);
      }
    } catch (error) {
      if (error.response && error.response.data) {
        const reader = new FileReader();

        reader.onload = () => {
          try {
            const err = JSON.parse(reader.result);
            toast.error(err.detail || "No data found.");
          } catch {
            toast.error("No data found.");
          }
        };

        reader.readAsText(error.response.data);
      } else {
        toast.error("Export failed.");
      }
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Download Report</Modal.Title>

        {title.toLowerCase() !== "attendence" &&
          title.toLowerCase() !== "allattendence" && (
            <Button
              variant="outline-primary"
              onClick={() => handleExport(true)}
              className="ms-auto"
            >
              Export All
            </Button>
          )}
      </Modal.Header>
      <Modal.Body>
        {!(
          (title.toLowerCase() === "tickets" &&
            selectedTicketIds?.length > 0) ||
          (title.toLowerCase() === "invoices" && selectedInvoiceIds?.length > 0)
        ) ? (
          <Form>
            {title.toLowerCase() === "leads" && (
              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={3} className="text-center">
                  Status
                </Form.Label>
                <Col sm={9}>
                  <select
                    className="form-control"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">— Select Status —</option>
                    {STATUS_CHOICES.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </Col>
              </Form.Group>
            )}

            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3} className="text-center">
                Select Dates
              </Form.Label>
              <Col sm={9}>
                <Row>
                  <Col>
                    <DatePicker
                      selected={startDate}
                      onChange={(date) => setStartDate(date)}
                      selectsStart
                      startDate={startDate}
                      endDate={endDate}
                      className="form-control me-2"
                    />
                  </Col>
                  -
                  <Col>
                    <DatePicker
                      selected={endDate}
                      onChange={(date) => setEndDate(date)}
                      selectsEnd
                      startDate={startDate}
                      endDate={endDate}
                      minDate={startDate}
                      className="form-control"
                    />
                  </Col>
                </Row>
              </Col>
            </Form.Group>
          </Form>
        ) : (
          <p className="text-center">
            Dates are disabled as specific tickets are selected.
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
        <Button
          variant="primary"
          onClick={() => handleExport(false)}
          disabled={exportLoading}
        >
          {exportLoading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />{" "}
              Exporting...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faFileExcel} className="me-2" />
              Download Excel
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ExportModal;
